import React, { useState } from "react";
import {
  Table,
  Button,
  Icon,
  Header,
  TextArea,
  Form,
  Dropdown,
  Checkbox,
  Input,
  Segment,
} from "semantic-ui-react";
import styled, { keyframes } from "styled-components";

/* 
  -------------
  SUB-COMPONENT: FollowUpModal (2nd-layer modal for factor follow-ups)
  -------------
*/

const FollowUpModal = ({
  open,
  onClose,
  followUp,
  onSave, // (updateFn) => { ... } to update the local followUp
  droppedItems,
  tempValue,
  setTempValue,
  addEqualityValue,
  removeEqualityValue,
}) => {
  if (!followUp) return null;

  const handleChange = (field, valOrFn) => {
    // We'll pass an updater to the parent, which has local followUp state
    onSave((prev) => {
      const newVal = typeof valOrFn === "function" ? valOrFn(prev[field]) : valOrFn;
      return { ...prev, [field]: newVal };
    });
  };

  return (
    <SemanticModal className={`ui modal ${open ? "active" : ""}`} open={open} onClose={onClose}>
      <div className="header">{followUp.followUpId ? "Edit Follow-Up" : "Add Follow-Up"}</div>
      <div className="content">
        <Form>
          <Form.Group widths="equal">
            <Form.Field>
              <label>AI Driven?</label>
              <Checkbox
                checked={followUp.isAiDriven}
                onChange={(e, { checked }) => handleChange("isAiDriven", checked)}
              />
            </Form.Field>
            <Form.Field>
              <label>Condition</label>
              <Dropdown
                selection
                options={[
                  { key: "avgScore", value: "averageScore", text: "Average Score" },
                  { key: "avgChange", value: "averageChange", text: "Average Change (%)" },
                ]}
                value={followUp.conditionType}
                onChange={(e, { value }) => handleChange("conditionType", value)}
              />
            </Form.Field>
            <Form.Field>
              <label>Operator</label>
              <Dropdown
                selection
                options={[
                  { key: "gt", value: ">", text: ">" },
                  { key: "lt", value: "<", text: "<" },
                  { key: "eq", value: "=", text: "=" },
                ]}
                value={followUp.operator}
                onChange={(e, { value }) => handleChange("operator", value)}
              />
            </Form.Field>
          </Form.Group>

          {/* Operator = => multi values; else single numeric value */}
          {followUp.operator === "=" ? (
            <div style={{ marginBottom: "1rem" }}>
              <label>Values (multiple)</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                {(followUp.values || []).map((val, idx) => (
                  <span
                    key={idx}
                    style={{
                      background: "#ddd",
                      padding: "0.25rem 0.5rem",
                      borderRadius: "1rem",
                    }}
                  >
                    {val}{" "}
                    <Icon
                      name="close"
                      style={{ cursor: "pointer" }}
                      onClick={() => removeEqualityValue(idx)}
                    />
                  </span>
                ))}
              </div>
              <Form.Field style={{ marginTop: "0.5rem" }}>
                <Input
                  type="number"
                  placeholder="Add value"
                  value={tempValue}
                  onChange={(e, { value }) => setTempValue(value)}
                  action={{
                    color: "blue",
                    icon: "plus",
                    onClick: () => {
                      if (tempValue.trim() !== "") {
                        addEqualityValue(tempValue);
                      }
                    },
                  }}
                />
              </Form.Field>
            </div>
          ) : (
            <Form.Field>
              <label>Value</label>
              <Input
                type="number"
                value={followUp.value}
                onChange={(e, { value }) => handleChange("value", parseFloat(value) || 0)}
              />
            </Form.Field>
          )}

          {/* # of Follow-Ups only if AI driven */}
          {followUp.isAiDriven && (
            <Form.Field>
              <label># of Follow-Ups</label>
              <Input
                type="number"
                value={followUp.numberOfFollowUps}
                onChange={(e, { value }) =>
                  handleChange("numberOfFollowUps", parseInt(value, 10) || 0)
                }
              />
            </Form.Field>
          )}

          {followUp.isAiDriven ? (
            <Form.Field>
              <label>AI Prompt</label>
              <Input
                placeholder="Enter AI prompt"
                value={followUp.aiPrompt}
                onChange={(e, { value }) => handleChange("aiPrompt", value)}
              />
            </Form.Field>
          ) : (
            <Form.Field>
              <label>Follow-up Question</label>
              <Dropdown
                placeholder="Select a question"
                selection
                clearable
                options={droppedItems.map((q) => {
                  const label =
                    q.properties?.label || q.properties?.placeholder || `Q ${q.uniqueId}`;
                  return { key: q.uniqueId, text: label, value: q.uniqueId };
                })}
                value={followUp.questionId}
                onChange={(e, { value }) => handleChange("questionId", value)}
              />
            </Form.Field>
          )}
        </Form>
      </div>
      <div className="actions">
        <Button onClick={onClose}>Cancel</Button>
        <Button primary onClick={onClose}>
          Save
        </Button>
      </div>
    </SemanticModal>
  );
};

/*
  -------------
  MAIN COMPONENT: FactorSettingsTab
  -------------
  The main list of Factors & Aggregate Follow-Ups, plus our two "drawer" components:
    - FactorDrawer
    - AggregateDrawer
  And the second-layer FollowUpModal for factor follow-ups.
*/

const FactorSettingsTab = ({
    droppedItems,
    setDroppedItems,
    factors,
    setFactors,
    aggregateFollowUps,
    setAggregateFollowUps,
  }) => {
  // 1) Factor state
  // Control factor drawer open
  const [factorDrawerOpen, setFactorDrawerOpen] = useState(false);
  const [activeFactor, setActiveFactor] = useState(null);

  // 2) Aggregate follow-ups state
  const [aggregateDrawerOpen, setAggregateDrawerOpen] = useState(false);
  const [activeAggregate, setActiveAggregate] = useState(null);

  // 3) Follow-up sub-modal (second-layer)
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false);
  const [activeFollowUp, setActiveFollowUp] = useState(null);
  // For the "operator='='" input
  const [tempEqualityValue, setTempEqualityValue] = useState("");


  // Example: add new factor
  const addNewFactor = () => {
    const newFactor = {
      factorId: `factor_${Date.now()}`,
      title: "",
      description: "",
      questionIds: [],
      followUps: [],
    };
    setFactors((prev) => [...prev, newFactor]);
  };

  // Example: remove factor
  const removeFactor = (factorId) => {
    setFactors((prev) => prev.filter((f) => f.factorId !== factorId));
  };

  // Similarly for aggregate follow-ups:
  const addAggregateFollowUp = () => {
    const newAgg = {
      followUpId: `agg_${Date.now()}`,
      isAiDriven: false,
      triggerType: "lowestScoringFactor",
      numberOfFollowUps: 0,
      followUpQuestion: "",
      aiPrompt: "",
    };
    setAggregateFollowUps((prev) => [...prev, newAgg]);
  };

  const removeAggregateFollowUp = (aggId) => {
    setAggregateFollowUps((prev) => prev.filter((a) => a.followUpId !== aggId));
  };

  /** -------------------
   *  Factor CRUD + Logic
   *  -------------------
   */
  const assignQuestionsToFactor = (questionIds, factorId) => {
    // A question can only belong to one factor
    setFactors((prev) =>
      prev.map((f) => {
        if (f.factorId === factorId) {
          return { ...f, questionIds };
        } else {
          const newQIds = f.questionIds.filter((id) => !questionIds.includes(id));
          return { ...f, questionIds: newQIds };
        }
      })
    );
  };

  const openFactorDrawer = (factor = null) => {
    if (factor) {
      setActiveFactor({ ...factor });
    } else {
      setActiveFactor({
        factorId: `factor_${Date.now()}`,
        title: "",
        description: "",
        questionIds: [],
        followUps: [],
      });
    }
    setFactorDrawerOpen(true);
  };

  const closeFactorDrawer = () => {
    setFactorDrawerOpen(false);
    setActiveFactor(null);
  };

  const saveFactor = () => {
    if (!activeFactor) return;
    setFactors((prev) => {
      const existing = prev.find((f) => f.factorId === activeFactor.factorId);
      if (existing) {
        // update
        return prev.map((f) => (f.factorId === activeFactor.factorId ? activeFactor : f));
      }
      // create new
      return [...prev, activeFactor];
    });
    assignQuestionsToFactor(activeFactor.questionIds, activeFactor.factorId);
    closeFactorDrawer();
  };

  

  /** -------------------
   *  Factor Follow-Ups
   *  -------------------
   */
  const openFollowUpModal = (fu = null) => {
    if (fu) {
      setActiveFollowUp({ ...fu });
    } else {
      setActiveFollowUp({
        followUpId: `followup_${Date.now()}`,
        isAiDriven: false,
        conditionType: "averageScore",
        operator: ">",
        value: 0,
        values: [],
        numberOfFollowUps: 0,
        questionId: "",
        aiPrompt: "",
      });
    }
    setTempEqualityValue("");
    setFollowUpModalOpen(true);
  };

  const closeFollowUpModal = () => {
    setFollowUpModalOpen(false);
    if (!activeFactor || !activeFollowUp) return;

    // If new => push; if existing => update
    setActiveFactor((prevFactor) => {
      const exists = prevFactor.followUps.find(
        (x) => x.followUpId === activeFollowUp.followUpId
      );
      let newFUs;
      if (exists) {
        newFUs = prevFactor.followUps.map((f) =>
          f.followUpId === activeFollowUp.followUpId ? activeFollowUp : f
        );
      } else {
        newFUs = [...prevFactor.followUps, activeFollowUp];
      }
      return { ...prevFactor, followUps: newFUs };
    });
  };

  // The modal passes a function to "onSave"
  const handleFollowUpSave = (updaterFn) => {
    setActiveFollowUp((prev) => {
      if (!prev) return prev;
      return updaterFn(prev);
    });
  };

  const removeFactorFollowUp = (fuId) => {
    if (!activeFactor) return;
    setActiveFactor((prev) => ({
      ...prev,
      followUps: prev.followUps.filter((x) => x.followUpId !== fuId),
    }));
  };

  // For operator="=", multi-value
  const addEqualityValue = (val) => {
    const numberVal = parseFloat(val);
    if (Number.isNaN(numberVal)) return;
    setActiveFollowUp((prev) => {
      if (!prev) return prev;
      return { ...prev, values: [...(prev.values || []), numberVal] };
    });
    setTempEqualityValue("");
  };

  const removeEqualityValue = (idx) => {
    setActiveFollowUp((prev) => {
      if (!prev) return prev;
      const copy = [...prev.values];
      copy.splice(idx, 1);
      return { ...prev, values: copy };
    });
  };

  /** -------------------
   *  Aggregate Follow-Ups
   *  -------------------
   */
  const openAggregateDrawer = (agg = null) => {
    if (agg) {
      setActiveAggregate({ ...agg });
    } else {
      setActiveAggregate({
        followUpId: `agg_${Date.now()}`,
        isAiDriven: false,
        triggerType: "lowestScoringFactor",
        numberOfFollowUps: 0,
        followUpQuestion: "",
        aiPrompt: "",
      });
    }
    setAggregateDrawerOpen(true);
  };

  const closeAggregateDrawer = () => {
    setAggregateDrawerOpen(false);
    setActiveAggregate(null);
  };

  const saveAggregate = () => {
    if (!activeAggregate) return;
    setAggregateFollowUps((prev) => {
      const existing = prev.find((x) => x.followUpId === activeAggregate.followUpId);
      if (existing) {
        return prev.map((x) => (x.followUpId === activeAggregate.followUpId ? activeAggregate : x));
      }
      return [...prev, activeAggregate];
    });
    closeAggregateDrawer();
  };


  /** -------------------
   *  Build question options
   *  -------------------
   */
  const questionOptions = droppedItems.map((q) => {
    const label =
      q.properties?.label || q.properties?.placeholder || `Question ${q.uniqueId}`;
    // If assigned to a different factor, disable
    const assignedElsewhere = factors.some(
      (f) => f.factorId !== activeFactor?.factorId && f.questionIds.includes(q.uniqueId)
    );
    return {
      key: q.uniqueId,
      text: label + (assignedElsewhere ? " (assigned)" : ""),
      value: q.uniqueId,
      disabled: assignedElsewhere,
    };
  });

  return (
    <div style={{ padding: "1rem" }}>
     <HeaderRow>
        <Header as="h2" style={{ marginBottom: 0 }}>
          Factor Settings
        </Header>
        <Button color="blue" onClick={() => openFactorDrawer(null)}>
          <Icon name="plus" />
          Add Factor
        </Button>
      </HeaderRow>
      <DescriptionText>
        Factors help group questions together, making your analysis more robust and
        allowing for deeper insights.
      </DescriptionText>

      <Table celled striped style={{ marginTop: "1rem" }}>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Factor Title</Table.HeaderCell>
            <Table.HeaderCell># Assigned Qs</Table.HeaderCell>
            <Table.HeaderCell># Follow-Ups</Table.HeaderCell>
            <Table.HeaderCell width={3}>Actions</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {factors.length === 0 && (
            <Table.Row>
              <Table.Cell colSpan="4" textAlign="center">
                No factors defined yet.
              </Table.Cell>
            </Table.Row>
          )}
          {factors.map((f) => (
            <Table.Row key={f.factorId}>
              <Table.Cell>{f.title || "(Untitled Factor)"}</Table.Cell>
              <Table.Cell>{f.questionIds.length}</Table.Cell>
              <Table.Cell>{f.followUps.length}</Table.Cell>
              <Table.Cell>
                <Button size="tiny" icon="edit" content="Edit" onClick={() => openFactorDrawer(f)} />
                <Button
                  size="tiny"
                  color="red"
                  icon="trash"
                  onClick={() => removeFactor(f.factorId)}
                />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      {/* AGGREGATE FOLLOW-UPS */}
      <SubHeaderRow>
        <Header as="h3" style={{ marginTop: 0, marginBottom: 0 }}>
          Aggregate Factor Follow-Ups
        </Header>
        <Button color="teal" onClick={() => openAggregateDrawer(null)}>
          <Icon name="plus" />
          Add Aggregate Follow-Up
        </Button>
      </SubHeaderRow>
      <DescriptionText>
        These rules trigger based on comparisons like “lowest scoring factor,” “biggest
        improvement,” etc.
      </DescriptionText>

      <Table celled striped style={{ marginTop: "1rem" }}>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Trigger Type</Table.HeaderCell>
            <Table.HeaderCell>AI Driven?</Table.HeaderCell>
            <Table.HeaderCell># of Follow-Ups</Table.HeaderCell>
            <Table.HeaderCell width={3}>Actions</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {aggregateFollowUps.length === 0 && (
            <Table.Row>
              <Table.Cell colSpan="4" textAlign="center">
                No aggregate follow-ups defined yet.
              </Table.Cell>
            </Table.Row>
          )}
          {aggregateFollowUps.map((agg) => (
            <Table.Row key={agg.followUpId}>
              <Table.Cell>{agg.triggerType}</Table.Cell>
              <Table.Cell>{agg.isAiDriven ? "Yes" : "No"}</Table.Cell>
              <Table.Cell>{agg.isAiDriven ? agg.numberOfFollowUps : "-"}</Table.Cell>
              <Table.Cell>
                <Button
                  size="tiny"
                  icon="edit"
                  content="Edit"
                  onClick={() => openAggregateDrawer(agg)}
                />
                <Button
                  size="tiny"
                  color="red"
                  icon="trash"
                  onClick={() => removeAggregateFollowUp(agg.followUpId)}
                />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      {/* ---------------------------
           1) FACTOR DRAWER
      ----------------------------*/}
      {factorDrawerOpen && (
        <>
          <DrawerOverlay onClick={closeFactorDrawer} />
          <DrawerPanel>
            <DrawerHeader>
              <DrawerTitle>
                {activeFactor?.factorId ? "Edit Factor" : "Add Factor"}
              </DrawerTitle>
              <Buttons>
                {activeFactor?.factorId && (
                  <DeleteButton
                    onClick={() => {
                      if (activeFactor?.factorId) {
                        removeFactor(activeFactor.factorId);
                      }
                      closeFactorDrawer();
                    }}
                  >
                    Remove
                  </DeleteButton>
                )}
                <CloseButton onClick={closeFactorDrawer}>Close</CloseButton>
              </Buttons>
            </DrawerHeader>
            <DrawerBody>
              {activeFactor && (
                <>
                  <Form>
                    <Form.Field>
                      <label>Title</label>
                      <Input
                        placeholder="Factor Title"
                        value={activeFactor.title}
                        onChange={(e, { value }) =>
                          setActiveFactor((prev) => ({ ...prev, title: value }))
                        }
                      />
                    </Form.Field>
                    <Form.Field>
                      <label>Description</label>
                      <TextArea
                        placeholder="Factor Description"
                        value={activeFactor.description}
                        onChange={(e, { value }) =>
                          setActiveFactor((prev) => ({ ...prev, description: value }))
                        }
                      />
                    </Form.Field>

                    <Form.Field>
                      <label>Assigned Questions</label>
                      <Dropdown
                        placeholder="Select Questions"
                        fluid
                        multiple
                        search
                        selection
                        options={questionOptions}
                        value={activeFactor.questionIds}
                        onChange={(e, { value }) =>
                          setActiveFactor((prev) => ({ ...prev, questionIds: value }))
                        }
                      />
                      <small style={{ color: "#666" }}>
                        A question can only be assigned to one factor at a time.
                      </small>
                    </Form.Field>
                  </Form>

                  <Segment style={{ marginTop: "1.5rem" }}>
                    <Header as="h4">Factor Follow-Ups</Header>
                    <Table compact>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>Condition</Table.HeaderCell>
                          <Table.HeaderCell>Operator</Table.HeaderCell>
                          <Table.HeaderCell>AI?</Table.HeaderCell>
                          <Table.HeaderCell># Values / Value</Table.HeaderCell>
                          <Table.HeaderCell />
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {activeFactor.followUps.length === 0 && (
                          <Table.Row>
                            <Table.Cell colSpan="5" textAlign="center">
                              No follow-ups defined.
                            </Table.Cell>
                          </Table.Row>
                        )}
                        {activeFactor.followUps.map((fu) => (
                          <Table.Row key={fu.followUpId}>
                            <Table.Cell>{fu.conditionType}</Table.Cell>
                            <Table.Cell>{fu.operator}</Table.Cell>
                            <Table.Cell>{fu.isAiDriven ? "Yes" : "No"}</Table.Cell>
                            <Table.Cell>
                              {fu.operator === "="
                                ? `${fu.values?.length || 0} values`
                                : `val=${fu.value}`}
                            </Table.Cell>
                            <Table.Cell>
                              <Button
                                size="tiny"
                                icon="edit"
                                onClick={() => openFollowUpModal(fu)}
                              />
                              <Button
                                size="tiny"
                                color="red"
                                icon="trash"
                                onClick={() => removeFactorFollowUp(fu.followUpId)}
                              />
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                    <Button
                      icon="plus"
                      content="Add Follow-Up"
                      onClick={() => openFollowUpModal(null)}
                    />
                  </Segment>
                </>
              )}
            </DrawerBody>
            <DrawerFooter>
              <PrimaryButton onClick={saveFactor}>Save Factor</PrimaryButton>
            </DrawerFooter>
          </DrawerPanel>
        </>
      )}

      {/* ---------------------------
           2) AGGREGATE DRAWER
      ----------------------------*/}
      {aggregateDrawerOpen && (
        <>
          <DrawerOverlay onClick={closeAggregateDrawer} />
          <DrawerPanel>
            <DrawerHeader>
              <DrawerTitle>
                {activeAggregate?.followUpId ? "Edit Aggregate Follow-Up" : "Add Aggregate Follow-Up"}
              </DrawerTitle>
              <Buttons>
                {activeAggregate?.followUpId && (
                  <DeleteButton
                    onClick={() => {
                      if (activeAggregate?.followUpId) {
                        removeAggregateFollowUp(activeAggregate.followUpId);
                      }
                      closeAggregateDrawer();
                    }}
                  >
                    Remove
                  </DeleteButton>
                )}
                <CloseButton onClick={closeAggregateDrawer}>Close</CloseButton>
              </Buttons>
            </DrawerHeader>
            <DrawerBody>
              {activeAggregate && (
                <Form>
                  <Form.Field>
                    <label>AI Driven?</label>
                    <Checkbox
                      checked={activeAggregate.isAiDriven}
                      onChange={(e, { checked }) =>
                        setActiveAggregate((prev) => ({ ...prev, isAiDriven: checked }))
                      }
                    />
                  </Form.Field>
                  <Form.Field>
                    <label>Trigger Type</label>
                    <Dropdown
                      selection
                      options={[
                        {
                          key: "lowest",
                          value: "lowestScoringFactor",
                          text: "Lowest Scoring Factor",
                        },
                        {
                          key: "highest",
                          value: "highestScoringFactor",
                          text: "Highest Scoring Factor",
                        },
                        {
                          key: "improve",
                          value: "biggestImprovement",
                          text: "Biggest Improvement",
                        },
                        {
                          key: "decrease",
                          value: "biggestDecrease",
                          text: "Biggest Decrease",
                        },
                      ]}
                      value={activeAggregate.triggerType}
                      onChange={(e, { value }) =>
                        setActiveAggregate((prev) => ({ ...prev, triggerType: value }))
                      }
                    />
                  </Form.Field>

                  {/* # of follow-ups only if AI-driven */}
                  {activeAggregate.isAiDriven && (
                    <>
                      <Form.Field>
                        <label># of Follow-Ups</label>
                        <Input
                          type="number"
                          value={activeAggregate.numberOfFollowUps}
                          onChange={(e, { value }) =>
                            setActiveAggregate((prev) => ({
                              ...prev,
                              numberOfFollowUps: parseInt(value, 10) || 0,
                            }))
                          }
                        />
                      </Form.Field>
                      <Form.Field>
                        <label>AI Prompt</label>
                        <Input
                          placeholder="Enter AI prompt"
                          value={activeAggregate.aiPrompt}
                          onChange={(e, { value }) =>
                            setActiveAggregate((prev) => ({ ...prev, aiPrompt: value }))
                          }
                        />
                      </Form.Field>
                    </>
                  )}

                  {/* If not AI-driven => text input for the question */}
                  {!activeAggregate.isAiDriven && (
                    <Form.Field>
                      <label>Follow-up Question</label>
                      <TextArea
                        placeholder="What follow-up question should be asked?"
                        value={activeAggregate.followUpQuestion}
                        onChange={(e, { value }) =>
                          setActiveAggregate((prev) => ({ ...prev, followUpQuestion: value }))
                        }
                      />
                    </Form.Field>
                  )}
                </Form>
              )}
            </DrawerBody>
            <DrawerFooter>
              <PrimaryButton onClick={saveAggregate}>Save</PrimaryButton>
            </DrawerFooter>
          </DrawerPanel>
        </>
      )}

      {/* ---------------------------
           SECOND-LAYER: FOLLOW-UP MODAL
      ----------------------------*/}
      <FollowUpModal
        open={followUpModalOpen}
        onClose={closeFollowUpModal}
        followUp={activeFollowUp}
        onSave={handleFollowUpSave}
        droppedItems={droppedItems}
        tempValue={tempEqualityValue}
        setTempValue={setTempEqualityValue}
        addEqualityValue={addEqualityValue}
        removeEqualityValue={removeEqualityValue}
      />
    </div>
  );
};

export default FactorSettingsTab;

/* ======================
   STYLED COMPONENTS for Drawer
   (inspired by your SettingsDrawer snippet)
   ====================== */

const HeaderRow = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: baseline;
   margin-bottom: 0.5rem;
 `;
 
 /**
  * Same layout but for the sub-header (Aggregate).
  */
 const SubHeaderRow = styled(HeaderRow)`
   margin-top: 2rem;
 `;
 
 /**
  * Simple paragraph styling
  */
 const DescriptionText = styled.p`
   margin: 0 0 1rem 0;
   color: #555;
   line-height: 1.4;
 `;

const SemanticModal = styled.div`
  /* This is just a minimal styling to approximate a Semantic UI modal overlay
     so we can use open/close. In a real setup, you'd rely on the built-in .ui.modal classes
     or fully style it yourself. */
  display: ${(props) => (props.open ? "block" : "none")};
  position: fixed;
  z-index: 2000;
  top: 50%;
  left: 50%;
  width: 600px;
  max-width: 90vw;
  background: #fff;
  transform: translate(-50%, -50%);
  border-radius: 6px;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.2);

  .header {
    font-size: 1.2rem;
    font-weight: 600;
    padding: 1rem;
    border-bottom: 1px solid #ddd;
  }
  .content {
    padding: 1rem;
  }
  .actions {
    padding: 0.75rem 1rem;
    border-top: 1px solid #ddd;
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
  }
`;

/** Dim overlay behind the drawer */
const DrawerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1000;
`;

/** Keyframe for the slide-in animation */
const slideInFromRight = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0%);
  }
`;

/** Panel that slides in from the right */
const DrawerPanel = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 520px;
  height: 100%;
  background: #fff;
  box-shadow: -2px 0 6px rgba(0, 0, 0, 0.1);
  z-index: 1001;
  display: flex;
  flex-direction: column;
  animation: ${slideInFromRight} 0.3s ease forwards;
`;

const DrawerHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const DrawerTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
`;

const Buttons = styled.div`
  display: flex;
  gap: 8px;
`;

const DeleteButton = styled.button`
  background: #f44336;
  color: white;
  border: none;
  padding: 6px 12px;
  cursor: pointer;
  border-radius: 4px;
`;

const CloseButton = styled.button`
  background: #999;
  color: white;
  border: none;
  padding: 6px 12px;
  cursor: pointer;
  border-radius: 4px;
`;

const DrawerBody = styled.div`
  flex: 1;
  padding: 16px;
  overflow-y: auto;
`;

const DrawerFooter = styled.div`
  padding: 16px;
  border-top: 1px solid #ddd;
  display: flex;
  justify-content: flex-end;
`;

const PrimaryButton = styled.button`
  background-color: #2d70e2;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background-color: #1c4db8;
  }
`;
